import { useState } from "react";
import { BadgeCheck, AlertTriangle, Loader2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Looks up the account holder's name with the bank and compares it to the employee's name.
export const BankAccountVerify = ({ form, set, result, onResult, inputCls }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const employeeName = [form.first_name, form.last_name, form.other_name].filter(Boolean).join(" ");
    const canCheck = form.bank_name && (form.bank_account_number || "").length === 10;

    const check = async () => {
        setBusy(true);
        setError("");
        try {
            const { data } = await api.post("/banks/verify-account", {
                bank_name: form.bank_name,
                account_number: form.bank_account_number,
                employee_name: employeeName,
            });
            set("bank_account_name", data.account_name);
            onResult({ ...data, for: `${form.bank_name}|${form.bank_account_number}` });
        } catch (err) {
            onResult(null);
            setError(formatApiError(err.response?.data?.detail) || "Could not verify the account");
        } finally {
            setBusy(false);
        }
    };

    const current = result && result.for === `${form.bank_name}|${form.bank_account_number}` ? result : null;

    return (
        <div className="md:col-span-6" data-testid="bank-verify">
            <div className="flex gap-2 items-end">
                <div className="flex-1">
                    <label className="overline block mb-2">Account name (from bank)</label>
                    <input
                        value={form.bank_account_name || ""}
                        onChange={(e) => set("bank_account_name", e.target.value)}
                        placeholder="Click Verify to fetch from the bank"
                        className={inputCls}
                        data-testid="input-bank-account-name"
                    />
                </div>
                <button
                    type="button"
                    onClick={check}
                    disabled={!canCheck || busy}
                    className="inline-flex items-center gap-1.5 border border-ink/20 px-4 py-2.5 text-sm hover:border-klein hover:text-klein disabled:opacity-40"
                    data-testid="bank-verify-button"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verify
                </button>
            </div>
            {current && current.match && (
                <p className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="bank-verify-match">
                    <BadgeCheck className="w-3.5 h-3.5" /> Bank name matches the employee ({current.account_name})
                </p>
            )}
            {current && !current.match && (
                <p className="mt-2 text-xs text-amber-700 inline-flex items-center gap-1" data-testid="bank-verify-mismatch">
                    <AlertTriangle className="w-3.5 h-3.5" /> The bank says "{current.account_name}", which doesn't match "{employeeName || "the employee"}". You can still save.
                </p>
            )}
            {error && <p className="mt-2 text-xs text-red-600" data-testid="bank-verify-error">{error}</p>}
        </div>
    );
};
